/**
 * The tool rail — `docs/property-maps-v2.md` §5.3, board 2.
 *
 * 64px, white, one column: Select · Pan · | Area · Rectangle · Circle · Line ·
 * | Marker · Label · | Measure, with Undo pinned at the bottom. Each tool is
 * 52px wide with an 18px glyph over a 9.5px label, and the active one wears
 * `accent-soft` with accent text.
 *
 * **A label under every glyph, not a tooltip.** Nine abstract shapes in a
 * column is a puzzle; nine shapes with their names under them is a rail
 * somebody reads once and then stops reading.
 *
 * **In view-only mode the rail is Select and Pan** (§5.7) — not nine greyed
 * tools. A tool you cannot use is noise, and the banner above has already
 * said why.
 *
 * The keyboard lives in `draw/index.ts`'s `TOOL_KEYS`, so the shortcut a rail
 * button advertises and the one the editor listens for are one table.
 */
import { cn } from '../../lib/cn.js'
import { MAP_TOOL_ICONS } from '../../frame/icons.js'
import type { ToolName } from '../draw/index.js'

interface RailTool {
  name: ToolName
  label: string
  /** The key that chooses it, shown in the title so it is discoverable. */
  key: string
}

const GROUPS: RailTool[][] = [
  [{ name: 'select', label: 'Select', key: 'V' }, { name: 'pan', label: 'Pan', key: 'H' }],
  [
    { name: 'area', label: 'Area', key: 'A' },
    { name: 'rect', label: 'Rectangle', key: 'R' },
    { name: 'circle', label: 'Circle', key: 'C' },
    { name: 'line', label: 'Line', key: 'L' },
  ],
  [{ name: 'marker', label: 'Marker', key: 'M' }, { name: 'label', label: 'Label', key: 'T' }],
  [{ name: 'measure', label: 'Measure', key: 'U' }],
]

/** Select and Pan only: §5.7's view-only rail. */
const LOOK_ONLY = GROUPS.slice(0, 1)

/**
 * The measuring set — §5.4's swap.
 *
 * Select, Pan and the four outline tools, and nothing else: a marker and a
 * label say something to a crew, which is a map's job, and a tape that
 * offered them would be offering to draw. Measure itself goes too, because
 * it is already in hand.
 */
const MEASURING = GROUPS.slice(0, 2)

export function ToolRail({
  tool, onPick, canDraw, offerMeasure, measuring, canUndo, onUndo, touch,
}: {
  tool: ToolName
  onPick: (tool: ToolName) => void
  canDraw: boolean
  /** The `measurements` feature. Off means the tool is absent, not greyed. */
  offerMeasure: boolean
  /** In a measure session, the rail is the measuring set (§5.4). */
  measuring?: boolean
  canUndo: boolean
  onUndo: () => void
  /**
   * §7: a finger at any width from 768px up. 52 × 48 rather than 52 × 44 —
   * the rail keeps its 64px and the glyph and label keep their sizes, so
   * what changes is the only thing that was short of a thumb.
   */
  touch?: boolean
}) {
  const base = measuring ? MEASURING : (canDraw ? GROUPS : LOOK_ONLY)
  const groups = base
    .map((group) => group.filter((one) => one.name !== 'measure' || offerMeasure))
    .filter((group) => group.length > 0)

  return (
    <div className={cn(
      'flex w-16 shrink-0 flex-col items-center gap-0.5 border-r border-line bg-surface py-2',
    )}
    >
      {groups.map((group, index) => (
        <div key={group[0]!.name} className="contents">
          {index > 0 && <div className="my-1 h-px self-stretch bg-line mx-2" aria-hidden />}
          {group.map((one) => (
            <RailButton
              key={one.name}
              one={one}
              active={tool === one.name}
              touch={touch}
              onPick={() => onPick(one.name)}
            />
          ))}
        </div>
      ))}

      {/*
        * Undo at the foot, away from the tools, because it is not a mode —
        * it undoes whatever the last one did (§5.3's 50-deep stack).
        */}
      <button
        type="button"
        onClick={onUndo}
        disabled={!canUndo}
        title="Undo the last change (⌘Z)"
        className={cn(
          'mt-auto flex w-[52px] flex-col items-center justify-center gap-[3px] rounded-button',
          'pb-1.5 pt-[7px] text-[9.5px] font-semibold',
          touch && 'min-h-12',
          canUndo ? 'text-text-body hover:bg-hover' : 'cursor-not-allowed text-text-faint',
        )}
      >
        <MAP_TOOL_ICONS.undo size={18} />
        Undo
      </button>
    </div>
  )
}

function RailButton({ one, active, touch, onPick }: {
  one: RailTool
  active: boolean
  touch?: boolean
  onPick: () => void
}) {
  const Icon = MAP_TOOL_ICONS[one.name as keyof typeof MAP_TOOL_ICONS]
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onPick}
      title={`${one.label} (${one.key})`}
      className={cn(
        'flex w-[52px] flex-col items-center justify-center gap-[3px] rounded-button',
        'pb-1.5 pt-[7px] text-[9.5px] font-semibold',
        touch && 'min-h-12',
        active ? 'bg-accent-soft text-accent' : 'text-text-body hover:bg-hover',
      )}
    >
      <Icon size={18} />
      {one.label}
    </button>
  )
}
